// Cognito Identity Pool credentials + SigV4 MQTT over WebSocket to IoT Core.
// Subscribe-only: the dashboard never publishes, and the device cert never
// leaves the device. Used for live UART capture and the decoded panel feed.

import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import { formatUrl } from '@aws-sdk/util-format-url';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';
import { SignatureV4 } from '@smithy/signature-v4';
import mqtt, { type MqttClient } from 'mqtt';
import { getIdToken } from './auth';

export interface IotSession {
  identityPoolId: string;
  iotEndpoint: string;
  region: string;
  topic: string;
}

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const URL_MAX_AGE_MS = 4 * 60 * 1000;
const URL_REFRESH_INTERVAL_MS = 4 * 60 * 1000;
const CONNECT_TIMEOUT_MS = 8000;
const SUBSCRIBE_TIMEOUT_MS = 8000;
const CREDENTIAL_REFRESH_BUFFER_MS = 60_000;

interface CachedCredentials {
  identityId: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiresAt: number;
}

async function cognitoCredentials(cfg: IotSession, cache: CachedCredentials | null) {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('not signed in');

  const provider = `cognito-idp.${cfg.region}.amazonaws.com/${import.meta.env.VITE_USER_POOL_ID}`;
  const logins = { [provider]: idToken };
  const client = new CognitoIdentityClient({ region: cfg.region });

  let identityId = cache?.identityId;
  if (!identityId) {
    const { IdentityId } = await client.send(
      new GetIdCommand({ IdentityPoolId: cfg.identityPoolId, Logins: logins })
    );
    if (!IdentityId) throw new Error('no Cognito identity');
    identityId = IdentityId;
  }

  if (cache && cache.identityId === identityId && cache.expiresAt > Date.now() + CREDENTIAL_REFRESH_BUFFER_MS) {
    return cache;
  }

  const { Credentials } = await client.send(
    new GetCredentialsForIdentityCommand({ IdentityId: identityId, Logins: logins })
  );
  if (!Credentials?.AccessKeyId || !Credentials.SecretKey || !Credentials.SessionToken) {
    throw new Error('no IoT credentials');
  }

  const expiresAt = Credentials.Expiration
    ? Credentials.Expiration.getTime()
    : Date.now() + 3600_000;

  return {
    identityId,
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiresAt,
  };
}

function iotHost(endpoint: string) {
  return endpoint.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

async function signedMqttUrl(cfg: IotSession, creds: CachedCredentials) {
  const hostname = iotHost(cfg.iotEndpoint);
  const signer = new SignatureV4({
    service: 'iotdevicegateway',
    region: cfg.region,
    sha256: Sha256,
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      // IoT Device Gateway will not accept a URL whose canonical request
      // includes X-Amz-Security-Token. Sign without it, then append below.
    },
  });

  const request = new HttpRequest({
    method: 'GET',
    protocol: 'wss:',
    hostname,
    path: '/mqtt',
    headers: { host: hostname },
  });

  const signed = await signer.presign(request, { expiresIn: 300 });
  const url =
    formatUrl(signed).replace(/:443(?=\/|\?|$)/, '') +
    `&X-Amz-Security-Token=${encodeURIComponent(creds.sessionToken)}`;
  return url;
}

export interface UartMqttHandle {
  disconnect: () => void;
  isConnected: () => boolean;
  ensureConnected: () => Promise<void>;
}

function mqttFail(err: unknown) {
  return err instanceof Error ? err : new Error(String(err));
}

interface SignedSession {
  url: string;
  clientId: string;
  signedAt: number;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function waitForConnect(client: MqttClient, timeoutMs = CONNECT_TIMEOUT_MS): Promise<void> {
  if (client.connected) return Promise.resolve();

  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => done(new Error('MQTT connect timed out')), timeoutMs);
    const done = (err?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      client.removeListener('connect', onConnect);
      client.removeListener('error', onError);
      client.removeListener('close', onClose);
      if (err) reject(err);
      else resolve();
    };
    const onConnect = () => done();
    const onError = (err: Error) => done(mqttFail(err));
    const onClose = () => done(new Error('MQTT connection closed before CONNACK'));
    client.once('connect', onConnect);
    client.once('error', onError);
    client.once('close', onClose);
  });
}

export async function subscribeUart(
  cfg: IotSession,
  onMessage: (payload: string) => void,
  onError: (err: Error) => void,
  onConnectionChange?: (connected: boolean) => void
): Promise<UartMqttHandle> {
  let disposed = false;
  let client: MqttClient | null = null;
  let reconnectTimer: number | null = null;
  let urlRefreshTimer: number | null = null;
  let reconnectPromise: Promise<void> | null = null;
  let reconnectAttempt = 0;
  let connectGeneration = 0;
  let credCache: CachedCredentials | null = null;
  let signed: SignedSession = { url: '', clientId: '', signedAt: 0 };
  let prefetchPromise: Promise<void> | null = null;

  const clearReconnectTimer = () => {
    if (reconnectTimer != null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const isConnected = () => !disposed && Boolean(client?.connected);

  const reconnectDelay = () =>
    Math.min(RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt, RECONNECT_MAX_DELAY_MS);

  const refreshSignedUrl = async (force = false) => {
    if (!force && signed.url && Date.now() - signed.signedAt < URL_MAX_AGE_MS) {
      return;
    }
    credCache = await cognitoCredentials(cfg, credCache);
    signed = {
      url: await signedMqttUrl(cfg, credCache),
      clientId: credCache.identityId,
      signedAt: Date.now(),
    };
  };

  const prefetchSignedUrl = (force = false) => {
    if (!force && prefetchPromise) return prefetchPromise;
    prefetchPromise = refreshSignedUrl(force).finally(() => {
      prefetchPromise = null;
    });
    return prefetchPromise;
  };

  const destroyClient = async (c: MqttClient | null) => {
    if (!c) return;
    c.removeAllListeners();
    try {
      await c.endAsync(true);
    } catch {
      /* already closed */
    }
    if (client === c) client = null;
  };

  const openClient = async (forceRefresh = false): Promise<void> => {
    await refreshSignedUrl(forceRefresh);
    const generation = ++connectGeneration;
    await destroyClient(client);

    const c = mqtt.connect(signed.url, {
      clientId: `${signed.clientId}-web-${generation}`,
      protocol: 'wss',
      protocolId: 'MQTT',
      protocolVersion: 4,
      clean: true,
      manualConnect: true,
      reconnectPeriod: 0,
      resubscribe: true,
      keepalive: 30,
      connectTimeout: CONNECT_TIMEOUT_MS,
      transformWsUrl: () => signed.url,
      createWebsocket: (wsUrl, protocols) => {
        const socket = new WebSocket(wsUrl, protocols);
        socket.binaryType = 'arraybuffer';
        return socket;
      },
    });

    client = c;

    c.on('message', (_topic, payload) => {
      if (generation !== connectGeneration) return;
      onMessage(payload.toString());
    });
    c.on('error', (err) => {
      if (generation !== connectGeneration) return;
      onError(mqttFail(err));
    });
    c.on('connect', () => {
      if (disposed || generation !== connectGeneration) return;
      reconnectAttempt = 0;
      clearReconnectTimer();
      onConnectionChange?.(true);
    });
    c.on('close', () => {
      if (disposed || generation !== connectGeneration || reconnectPromise) return;
      if (client !== c) return;
      client = null;
      onConnectionChange?.(false);
      prefetchSignedUrl(true).catch(() => undefined);
      beginReconnect();
    });

    const connectWait = waitForConnect(c);
    c.connect();
    await connectWait;

    await withTimeout(
      new Promise<void>((resolve, reject) => {
        c.subscribe(cfg.topic, { qos: 1 }, (err) => {
          if (err) reject(err);
          else resolve();
        });
      }),
      SUBSCRIBE_TIMEOUT_MS,
      'MQTT subscribe'
    );
  };

  const scheduleReconnect = () => {
    if (disposed || reconnectTimer != null || reconnectPromise || isConnected()) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (isConnected()) return;
      ensureConnected()
        .then(() => {
          reconnectAttempt = 0;
        })
        .catch(() => {
          reconnectAttempt++;
          if (!disposed) scheduleReconnect();
        });
    }, reconnectDelay());
  };

  const ensureConnected = async () => {
    if (disposed) throw new Error('MQTT disconnected');
    if (isConnected()) return;
    if (reconnectPromise) return reconnectPromise;

    reconnectPromise = (async () => {
      if (prefetchPromise) await prefetchPromise.catch(() => undefined);
      await openClient(true);
    })().finally(() => {
      reconnectPromise = null;
    });

    return reconnectPromise;
  };

  const beginReconnect = () => {
    ensureConnected().catch(() => {
      reconnectAttempt++;
      scheduleReconnect();
    });
  };

  try {
    await openClient(true);
  } catch (err) {
    await destroyClient(client);
    throw mqttFail(err);
  }

  onConnectionChange?.(true);

  urlRefreshTimer = window.setInterval(() => {
    prefetchSignedUrl(false).catch((err) => onError(mqttFail(err)));
  }, URL_REFRESH_INTERVAL_MS);

  return {
    disconnect: () => {
      disposed = true;
      connectGeneration++;
      clearReconnectTimer();
      if (urlRefreshTimer != null) {
        window.clearInterval(urlRefreshTimer);
        urlRefreshTimer = null;
      }
      void destroyClient(client);
      client = null;
      credCache = null;
      signed = { url: '', clientId: '', signedAt: 0 };
      onConnectionChange?.(false);
    },
    isConnected,
    ensureConnected,
  };
}
